import { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const SUGGESTIONS = [
  'How is the ecosystem health?',
  'Which product needs attention?',
  'What are the latest security issues?',
  'Show me the deploy activity',
];

const BASE = import.meta.env.VITE_API_URL || '';

export function ElvyraChatPanel({ onClose }: { onClose: () => void }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || streaming) return;

    const userMsg: Message = { role: 'user', content: text.trim() };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput('');
    setStreaming(true);

    try {
      const res = await fetch(`${BASE}/api/elvyra/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: history }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.error || res.statusText}` }]);
        setStreaming(false);
        return;
      }

      // Parse SSE stream
      const reader = res.body?.getReader();
      if (!reader) {
        setMessages(prev => [...prev, { role: 'assistant', content: 'No response stream available.' }]);
        setStreaming(false);
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';

      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const event = JSON.parse(data);
            if (event.type === 'content_block_delta' && event.delta?.text) {
              fullText += event.delta.text;
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', content: fullText };
                return updated;
              });
            }
          } catch {}
        }
      }

      if (!fullText) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: 'No response generated.' };
          return updated;
        });
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Connection error: ${(err as Error).message}` }]);
    } finally {
      setStreaming(false);
    }
  }, [messages, streaming]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <div
      className="fixed bottom-24 right-6 z-[200] w-[400px] max-w-[calc(100vw-48px)] h-[560px] max-h-[calc(100vh-140px)] bg-bg-surface border border-border rounded-lg shadow-2xl shadow-black/40 flex flex-col overflow-hidden animate-[fadeSlideIn_0.15s_ease-out]"
      role="dialog"
      aria-label="Elvyra Chat"
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border shrink-0">
        <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center font-display text-sm font-bold text-white shrink-0">
          E
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[0.85rem] font-bold">Elvyra</div>
          <div className="text-[0.62rem] text-text-muted">Online — watching your ecosystem</div>
        </div>
        <button
          onClick={onClose}
          className="w-7 h-7 rounded flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-bg-elevated transition-colors"
          aria-label="Close"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-4 h-4">
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
        {messages.length === 0 && (
          <div className="text-center py-6">
            <div className="w-12 h-12 rounded-full bg-accent/15 flex items-center justify-center mx-auto mb-3">
              <span className="font-display text-xl font-bold text-accent-bright">E</span>
            </div>
            <p className="text-[0.78rem] text-text-secondary mb-4">
              I'm watching your ecosystem. Ask me about health scores, security, deploys, or quality trends.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {SUGGESTIONS.map(s => (
                <button
                  key={s}
                  onClick={() => sendMessage(s)}
                  className="px-3 py-1.5 rounded-full bg-accent-soft text-accent-bright text-[0.68rem] font-medium border border-accent-border hover:bg-accent/20 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 text-[0.78rem] leading-relaxed ${
                m.role === 'user'
                  ? 'bg-accent text-white rounded-br-sm'
                  : 'bg-bg-elevated text-text-secondary rounded-bl-sm'
              }`}
            >
              {m.role === 'assistant' ? (
                <div className="whitespace-pre-wrap">{m.content || (streaming && i === messages.length - 1 ? '...' : '')}</div>
              ) : (
                m.content
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="px-4 py-3 border-t border-border flex gap-2 shrink-0">
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Ask Elvyra about your ecosystem..."
          disabled={streaming}
          className="flex-1 bg-bg-elevated border border-border rounded-md px-3 py-2 text-[0.78rem] text-text-primary placeholder:text-text-muted outline-none focus:border-accent transition-colors disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!input.trim() || streaming}
          className="w-9 h-9 rounded-md bg-accent text-white flex items-center justify-center hover:bg-accent-bright transition-colors disabled:opacity-30"
          aria-label="Send"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <path d="M14 2L7 9M14 2l-5 12-2-5-5-2 12-5z" />
          </svg>
        </button>
      </form>
    </div>
  );
}
